import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { BsCloudUpload, BsEye, BsFileEarmarkText } from "react-icons/bs";
import toast from "react-hot-toast";
import "../../CommonCss/form.css";
import DocumentPreviewModal from "../DocumentPreviewModal";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import { editEmployee, getReportingCandidates, getAssignableRoles } from "../../api/services/hroneApi";
import "./index.css";
import { ErrorText } from "../../StyledCss/styledComponents";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";
import { parseResponseBody } from "../../util/apiResponse";
import { isHiddenRole } from "../../util/roleConstants";

const AADHAR_INPUT_ID = "edit-employee-aadhar-upload";
const PAN_INPUT_ID = "edit-employee-pan-upload";
const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024;

const EditEmployeeModal = (props) => {
  const {
    closeEditEmployeeModel,
    singleEmployeeData,
    notifyUpdated,
    fetchEmployees,
  } = props;
  const [dropdown, setDropDown] = useState("none");
  const [reportingCandidates, setReportingCandidates] = useState([]);
  const [reportingRole, setReportingRole] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [updatedCertificates, setUpdatedCertificates] = useState({
    tenth: singleEmployeeData.tenth ? true : null,
    intermediate: singleEmployeeData.intermediate ? true : null,
    degree: singleEmployeeData.degree ? true : null,
    masters: singleEmployeeData.masters ? true : null,
    originalDegree: singleEmployeeData.originalDegree ? true : null,
  });
  const [aadharFile, setAadharFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const { hasPermission } = usePermissions();
  const canViewSalary = hasPermission(FEATURE_CODES.EMPLOYEE_SALARY_VIEW);
  const [assignableRoles, setAssignableRoles] = useState([]);
  // Role names offered in the "Reporting To" role dropdown — every role whose
  // show_reporting_to_role flag is on, most senior first.
  const [reportingToRoleNames, setReportingToRoleNames] = useState([]);

  useEffect(() => {
    const loadAssignableRoles = async () => {
      try {
        const response = await getAssignableRoles();
        const { ok, data } = await parseResponseBody(response);
        if (!ok || !data?.roles) return;
        // Which roles this user may hand out is decided by the backend from
        // role_assignable_roles (Role Permissions -> "Roles this role can
        // assign"), not by rank any more — the server flags each role it
        // returns.
        setAssignableRoles(
          data.roles.filter(
            (r) => r.is_assignable_by_current_role && !isHiddenRole(r.name),
          )
        );
        // Deliberately NOT rank-filtered: which roles can be reported to is
        // decided by each role's own show_reporting_to_role flag (set on the
        // Role Permissions screen), and a reporting manager is normally more
        // senior than any role the current user is allowed to assign.
        setReportingToRoleNames(
          [...data.roles]
            .filter((r) => r.show_reporting_to_role && !isHiddenRole(r.name))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((r) => r.name)
        );
      } catch (err) {
        // leave assignableRoles empty; the role dropdown just won't populate
      }
    };
    loadAssignableRoles();
  }, []);

  const pickFile = (inputId) => document.getElementById(inputId)?.click();

  const validatePdfAndSet = (e, setFile) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext !== "pdf" || file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error("File size should not exceed 1MB");
      e.target.value = "";
      return;
    }
    setFile(file);
  };

  const dropdownRef = useRef(null);
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropDown("none");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // const orgDetails = JSON.parse(localStorage.getItem("org"));
  // const { primaryColor } = orgDetails;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  // The role <select>'s options load asynchronously (after assignableRoles
  // resolves), so the native defaultValue can't match an option at mount
  // time — set the field's value explicitly once the options are in.
  useEffect(() => {
    if (assignableRoles.length > 0 && singleEmployeeData.role) {
      setValue("role", singleEmployeeData.role);
    }
  }, [assignableRoles, singleEmployeeData.role, setValue]);

  const employementStatus = watch("employeeStatus", singleEmployeeData.employementStatus);

  useEffect(() => {
    const fetchReportingCandidates = async () => {
      try {
        const response = await getReportingCandidates();
        if (response.status === 200) {
          const data = await response.json();
          const candidates = Array.isArray(data) ? data : [];
          setReportingCandidates(candidates);
          const currentCandidate = candidates.find(
            (candidate) => candidate.email === singleEmployeeData.reportingTo,
          );
          if (currentCandidate) {
            setReportingRole(currentCandidate.role);
          }
        }
      } catch (error) {}
    };
    fetchReportingCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportingEmailsForRole = reportingCandidates
    .filter((candidate) => candidate.role === reportingRole)
    .map((candidate) => candidate.email);

  // The "Reporting To" <select>'s options depend on reportingRole, which is
  // itself set asynchronously after fetchReportingCandidates resolves — set
  // the field's value only once the matching option actually exists.
  useEffect(() => {
    if (
      singleEmployeeData.reportingTo &&
      reportingEmailsForRole.includes(singleEmployeeData.reportingTo)
    ) {
      setValue("reportingTo", singleEmployeeData.reportingTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingEmailsForRole.join(","), singleEmployeeData.reportingTo]);

  // The employee's currently saved reporting role stays in the list even if
  // that role has since been un-flagged, so opening Edit on an existing
  // employee can never silently blank out their reporting manager. Newly
  // un-flagged roles are still absent for everyone else.
  const reportingRoleOptions = useMemo(() => {
    if (!reportingRole || reportingToRoleNames.includes(reportingRole)) {
      return reportingToRoleNames;
    }
    return [...reportingToRoleNames, reportingRole];
  }, [reportingToRoleNames, reportingRole]);

  const onChangeReportingRole = (e) => {
    setReportingRole(e.target.value);
    setValue("reportingTo", "");
  };

  const onSelectCertificates = (e) => {
    const name = e.target.name;
    if (e.target.checked) {
      setUpdatedCertificates({ ...updatedCertificates, [name]: true });
    } else {
      // delete updatedCertificates[name]
      setUpdatedCertificates({ ...updatedCertificates, [name]: null });
    }
  };

  const onSubmit = (data) => {
    data.id = singleEmployeeData.id;
    // The Salary field isn't rendered for users without salary-view
    // permission, so react-hook-form never registers it — carry the
    // employee's existing value through instead of submitting nothing
    // (the backend requires the key to be present).
    if (!canViewSalary) {
      data.salary = singleEmployeeData.salary;
    }
    handleUpdateEmployeeForm(data);
  };

  const handleUpdateEmployeeForm = async (data) => {
    try {
      const response = await editEmployee(data, updatedCertificates, { aadharFile, panFile });
      if (response.ok) {
        closeEditEmployeeModel();
        notifyUpdated();
        fetchEmployees();
      }
      else{
             const data=await response.json();
             setErrorMsg(data.detail || "Failed to update employee. Please try again.");

      }
    } catch(err) {
      console.error("Error updating employee:", err);
    }
  };

  const toggleDropdown = () => {
    if (dropdown === "none") {
      setDropDown("block");
    } else {
      setDropDown("none");
    }
  };

  const convertToMonths = (value) => {
    if (!value && value !== 0) return "";
    const [years, months = 0] = String(value).split(".").map(Number);
    return years * 12 + months;
  };

  const formatDate = (date) => {
    const splitDate = date.split("-");
    return `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`;
  };

  // const handleEmploymentStatusChange = (e) => {
  //   setEmployementStatus(e.target.value);
  // };

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      className="-Employee-modal"
    >
      <div className="edit-employee-form-container" autoComplete="off">
      <p className="edit-employee-section-title">Personal Information</p>
      <div className="edit-employee-grid">
        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.firstName}
              style={errors.firstName && { borderLeft: "10px solid red" }}
              type="text"
              {...register("firstName", {
                required: true,
                pattern: /^(?!\s)[a-zA-Z\s]{3,45}$/,
              })}
            />
            <StyledLabel className="user-label">First Name*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.lastName}
              style={errors.lastName && { borderLeft: "10px solid red" }}
              type="text"
              {...register("lastName", {
                required: true,
                pattern: /^(?!\s)[a-zA-Z\s]{3,45}$/,
              })}
            />
            <StyledLabel className="user-label">Last Name*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.id}
              disabled
              style={errors.id && { borderLeft: "10px solid red" }}
              type="text"
              {...register("id", {
                required: false,
                pattern: /^(?!\s)[a-zA-Z0-9]{3,10}$/,
              })}
            />
            <StyledLabel className="user-label" hasValue={true}
>Employee ID*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.email}
              style={errors.email && { borderLeft: "10px solid red" }}
              type="text"
              {...register("email", {
                required: true,
                pattern:
                  /^(?!\s)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              })}
            />
            <StyledLabel className="user-label">Email*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>
        <StyledInputContainer>
                <StyledInput
                  defaultValue={singleEmployeeData.personalEmail || ""}
                  style={errors.personalEmail && { borderLeft: "10px solid red" }}
                  type="email"
                  {...register("personalEmail", {
                    required: true,
                    pattern: /^(?!\s)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  })}
                />
                <StyledLabel className="user-label">Personal Email*</StyledLabel>
              </StyledInputContainer>

        <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.role}
              style={errors.role && { borderLeft: "10px solid red" }}
              id="role"
              name="select"
              {...register("role", { required: true })}
            >
              <option value="">---Select Role---</option>
              {assignableRoles.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name}
                </option>
              ))}
              {singleEmployeeData.role &&
                !assignableRoles.some((r) => r.name === singleEmployeeData.role) && (
                  <option value={singleEmployeeData.role}>{singleEmployeeData.role}</option>
                )}
            </StyledSelect>
            <StyledLabel className="user-label">Role*</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>

        <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.gender}
              style={errors.gender && { borderLeft: "10px solid red" }}
              id="gender"
              {...register("gender", { required: true })}
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Others">Others</option>
            </StyledSelect>
            <StyledLabel className="user-label">Gender*</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={formatDate(singleEmployeeData.dateOfBirth)}
              style={errors.dob && { borderLeft: "10px solid red" }}
              type="date"
              max="9999-12-31"
              {...register("dob", { required: true })}
            />
            <StyledLabel className="user-label">DOB*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={formatDate(singleEmployeeData.joiningDate)}
              style={errors.joiningDate && { borderLeft: "10px solid red" }}
              type="date"
              max="9999-12-31"
              {...register("joiningDate", { required: true })}
            />
            <StyledLabel className="user-label">Joining Date*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>
              <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
              defaultValue={convertToMonths(singleEmployeeData.pastExperience)}

                style={errors.pastExperience && { borderLeft: "10px solid red" }}
                type="number"
                min={0}
                {...register("pastExperience", {
                  required: true,
                  pattern: /^[0-9]{1,3}$/,

                })}
              />
              <StyledLabel className="user-label">Past Experience (Months)*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
        <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.qualifications}
              style={errors.qualifications && { borderLeft: "10px solid red" }}
              id="qualifications"
              {...register("qualifications", { required: true })}
            >
              <option value="">---select---</option>
              <option value="Degree">Degree</option>
              <option value="B.Tech">B.Tech</option>
              <option value="PG">PG</option>
              <option value="Other">Other</option>
            </StyledSelect>
            <StyledLabel className="user-label">Qualifications*</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.department}
              style={errors.department && { borderLeft: "10px solid red" }}
              type="text"
              {...register("department", {
                required: true,
                pattern: /^(?!\s)[a-zA-Z.\s-]{3,45}$/,
              })}
            />
            <StyledLabel className="user-label">Department*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.mobileNumber}
              style={errors.mobileNumber && { borderLeft: "10px solid red" }}
              type="tel"
              {...register("mobileNumber", {
                required: true,
                maxLength: 10,
                pattern: /[0-9]{10}/,
              })}
            />
            <StyledLabel className="user-label">Mobile No*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.bloodGroup}
              style={errors.bloodGroup && { borderLeft: "10px solid red" }}
              id="bloodGroup"
              {...register("bloodGroup", { required: true })}
            >
              <option className="option-bg" value="">
                ---select---
              </option>
              <option className="option-bg" value="A+">
                A+
              </option>
              <option value="B+">B+</option>
              <option value="AB+">AB+</option>
              <option value="O+">O+</option>
              <option value="A-">A-</option>
              <option value="B-">B-</option>
              <option value="AB-">AB-</option>
              <option value="O-">O-</option>
            </StyledSelect>
            <StyledLabel className="user-label">Blood Group*</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.designation}
              style={errors.designation && { borderLeft: "10px solid red" }}
              type="tel"
              {...register("designation", {
                required: true,
                pattern: /^(?!\s)[A-Za-z.\-_ ]{2,45}$/,
              })}
            />
            <StyledLabel className="user-label">Designation*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        {canViewSalary ? (
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                defaultValue={singleEmployeeData.salary}
                style={errors.salary && { borderLeft: "10px solid red" }}
                id="salary"
                type="number"
                min="0"
                {...register("salary", { required: true })}
              />
              <StyledLabel className="user-label">Salary*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
        ) : null}
      </div>

      <p className="edit-employee-section-title">Official Information</p>
      <div className="edit-employee-grid">
        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              defaultValue={singleEmployeeData.address}
              style={errors.location && { borderLeft: "10px solid red" }}
              type="text"
              {...register("location", {
                required: true,
                pattern: /^(?!\s)[A-Za-z ]{3,45}$/i,
              })}
            />
            <StyledLabel className="user-label">Location*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              id="bankName"
              defaultValue={singleEmployeeData.bankName}
              style={errors.bankName && { borderLeft: "10px solid red" }}
              type="text"
              {...register("bankName", {
                pattern: /^(?!\s)[a-zA-Z ]{3,45}$/,
              })}
            />
            <StyledLabel className="user-label">Bank Name</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={
                errors.bankAccountNumber && { borderLeft: "10px solid red" }
              }
              defaultValue={singleEmployeeData.bankAccountNumber}
              id="bankAccountNumber"
              type="tel"
              {...register("bankAccountNumber", {
                minLength: 11,
                maxLength: 20,
                pattern: /^(?!\s)[0-9]/,
              })}
            />
            <StyledLabel className="user-label">Bank Ac Number</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.ifsc && { borderLeft: "10px solid red" }}
              id="ifsc"
              defaultValue={singleEmployeeData.ifsc}
              type="text"
              {...register("ifsc", {
                pattern: /^(?!\s)[a-zA-Z0-9]{10,15}$/,
              })}
            />
            <StyledLabel className="user-label">IFSC</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.panNumber && { borderLeft: "10px solid red" }}
              defaultValue={singleEmployeeData.panNumber}
              id="panNumber"
              type="text"
              {...register("panNumber", {
                required: true,
                pattern: /^(?!\s)[a-zA-Z0-9]{10}$/,
              })}
            />
            <StyledLabel className="user-label">PAN Number*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.uanNumber && { borderLeft: "10px solid red" }}
              defaultValue={singleEmployeeData.uanNumber}
              id="uanNumber"
              type="text"
              {...register("uanNumber", {
                required: false,
              })}
            />
            <StyledLabel className="user-label">UAN Number</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.pfNumber && { borderLeft: "10px solid red" }}
              defaultValue={singleEmployeeData.pfNumber}
              id="pfNumber"
              type="text"
              {...register("pfNumber", {
                required: false,
              })}
            />
            <StyledLabel className="user-label">PF Number</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.healthCardNumber && { borderLeft: "10px solid red" }}
              defaultValue={singleEmployeeData.healthCardNumber}
              id="healthCardNumber"
              type="text"
              {...register("healthCardNumber", {
                required: false,
              })}
            />
            <StyledLabel className="user-label">Health Card Number</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>


        <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.employementStatus}
              id="employeeStatus"
              {...register("employeeStatus", { required: true })}
            >
              <option className="option-bg" value="Active">
                Active
              </option>
              <option value="Released">Released</option>
            </StyledSelect>
            <StyledLabel className="user-label">Employee Status*</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>
        {employementStatus === "Released" && (
          <>
            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  defaultValue={
                    singleEmployeeData.releaseDate
                      ? new Date(singleEmployeeData.releaseDate).toISOString().split("T")[0]
                      : ""
                  }
                  style={errors.releaseDate && { borderLeft: "10px solid red" }}
                  type="date"
                  max={new Date().toISOString().split("T")[0]} // Set max to today's date
                  {...register("releaseDate", {
                    required: employementStatus === "Released",
                  shouldUnregister: true,
                  })}
                />
                <StyledLabel className="user-label">Release Date*</StyledLabel>
              </StyledInputContainer>
            </StyledInputGroup>

            <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.releasedType}
              style={errors.releasedType && { borderLeft: "10px solid red" }}
              id="releasedType"
              {...register("releasedType", { required: true })}
            >
              <option value="">---select---</option>
              <option value="normal">Normal</option>
              <option value="abscond">Abscond</option>
            </StyledSelect>
            <StyledLabel className="user-label">Released Type*</StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>
            </>
        )}




        <StyledSelectGroup>
          <StyledSelectContainer>
            <StyledSelect
              defaultValue={singleEmployeeData.employeeProjectStatus}
              className="select-element"
              id="employeeProjectStatus"
              {...register("employeeProjectStatus", { required: true })}
            >
              <option className="option-bg" value="Active">
                Active
              </option>
              <option value="On Bench">On Bench</option>
            </StyledSelect>
            <StyledLabel className="user-label">
              Employee Project Status*
            </StyledLabel>
          </StyledSelectContainer>
        </StyledSelectGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                className="select-element"
                id="reportingToRole"
                value={reportingRole}
                onChange={onChangeReportingRole}
              >
                <option className="option-bg" value="">
                  ----Select----
                </option>
                {reportingRoleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Reporting To Role*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>

          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={errors.reportingTo && { borderLeft: "10px solid red" }}
                className="select-element"
                id="reportingTo"
                {...register("reportingTo")}
              >
                <option className="option-bg" value="">
                  ----Select----
                </option>
                {reportingEmailsForRole.map((email) => (
                  <option key={email} value={email}>
                    {email}
                  </option>
                ))}
              </StyledSelect>
              <StyledLabel className="user-label">Reporting To*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
      </div>

        {/* certificates + documents row */}
        <div className="edit-employee-docs-row">
        <div className="edit-employee-kyc-field" ref={dropdownRef}>
          <span className="edit-employee-kyc-label">&nbsp;</span>
          <div className="dropdown-btn-wrap">
            <button
              className="dropdown-btn"
              onClick={toggleDropdown}
              type="button"
              style={{
                border: `1px solid #000000`,
              }}
            >
              Certificates <MdOutlineKeyboardArrowDown />
            </button>
            <ul
              className="certificates-dropdown shadow"
              style={{
                display: `${dropdown}`,
                // border: `1px solid ${primaryColor}`,
              }}
            >
              <li>
                <label>
                  <input
                    onChange={onSelectCertificates}
                    checked={updatedCertificates.tenth ? true : null}
                    name="tenth"
                    value="10th"
                    type="checkbox"
                  />{" "}
                  10th
                </label>
              </li>
              <li>
                <label>
                  <input
                    checked={updatedCertificates.intermediate ? true : null}
                    onChange={onSelectCertificates}
                    type="checkbox"
                    value="intermediate"
                    name="intermediate"
                  />{" "}
                  Intermediate
                </label>
              </li>
              <li>
                <label>
                  <input
                    checked={updatedCertificates.degree ? true : null}
                    onChange={onSelectCertificates}
                    type="checkbox"
                    name="degree"
                    value="Degree"
                  />{" "}
                  Degree
                </label>
              </li>
              <li>
                <label>
                  <input
                    onChange={onSelectCertificates}
                    checked={updatedCertificates.masters ? true : null}
                    type="checkbox"
                    name="masters"
                    value="masters"
                  />{" "}
                  PG
                </label>
              </li>

              <li>
                <label>
                  <input
                    checked={updatedCertificates.originalDegree ? true : null}
                    onChange={onSelectCertificates}
                    value="original Degree"
                    type="checkbox"
                    name="originalDegree"
                  />{" "}
                  Original Degree
                </label>
              </li>
            </ul>
          </div>
        </div>

        <div className="edit-employee-kyc-field">
          <span className="edit-employee-kyc-label">Aadhar Card</span>
          <div className="edit-employee-kyc-row">
            <div className="edit-employee-kyc-box">
              <BsFileEarmarkText size={14} className="edit-employee-kyc-file-icon" />
              <span className={(aadharFile || singleEmployeeData.aadharCardUrl) ? "edit-employee-kyc-status-text edit-employee-kyc-status-text--uploaded" : "edit-employee-kyc-status-text"}>
                {aadharFile ? aadharFile.name : singleEmployeeData.aadharCardUrl ? "Uploaded Successfully" : "Not Uploaded"}
              </span>
            </div>
            <button
              type="button"
              className="edit-employee-kyc-icon-btn"
              disabled={!singleEmployeeData.aadharCardUrl}
              title="View"
              onClick={() => setPreviewDoc({ title: "Aadhar Card", url: singleEmployeeData.aadharCardUrl })}
            >
              <BsEye size={15} />
            </button>
            <input
              id={AADHAR_INPUT_ID}
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              onChange={(e) => validatePdfAndSet(e, setAadharFile)}
            />
            <button
              type="button"
              className="edit-employee-kyc-icon-btn"
              title={singleEmployeeData.aadharCardUrl ? "Replace" : "Upload"}
              onClick={() => pickFile(AADHAR_INPUT_ID)}
            >
              <BsCloudUpload size={15} />
            </button>
          </div>
        </div>

        <div className="edit-employee-kyc-field">
          <span className="edit-employee-kyc-label">PAN Card</span>
          <div className="edit-employee-kyc-row">
            <div className="edit-employee-kyc-box">
              <BsFileEarmarkText size={14} className="edit-employee-kyc-file-icon" />
              <span className={(panFile || singleEmployeeData.panCardUrl) ? "edit-employee-kyc-status-text edit-employee-kyc-status-text--uploaded" : "edit-employee-kyc-status-text"}>
                {panFile ? panFile.name : singleEmployeeData.panCardUrl ? "Uploaded Successfully" : "Not Uploaded"}
              </span>
            </div>
            <button
              type="button"
              className="edit-employee-kyc-icon-btn"
              disabled={!singleEmployeeData.panCardUrl}
              title="View"
              onClick={() => setPreviewDoc({ title: "PAN Card", url: singleEmployeeData.panCardUrl })}
            >
              <BsEye size={15} />
            </button>
            <input
              id={PAN_INPUT_ID}
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              onChange={(e) => validatePdfAndSet(e, setPanFile)}
            />
            <button
              type="button"
              className="edit-employee-kyc-icon-btn"
              title={singleEmployeeData.panCardUrl ? "Replace" : "Upload"}
              onClick={() => pickFile(PAN_INPUT_ID)}
            >
              <BsCloudUpload size={15} />
            </button>
          </div>
        </div>
        </div>
      </div>
      {errorMsg && (
                <ErrorText className="mt-3">*{errorMsg}</ErrorText>
              )}

      <div className="-emp-submit-btn gap-2">
        <SecondaryButton type="button" onClick={closeEditEmployeeModel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit">Submit</PrimaryButton>
      </div>

      {previewDoc && (
        <DocumentPreviewModal
          title={previewDoc.title}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </form>
  );
};

export default EditEmployeeModal;
